import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { CategoryId, QuizMode } from '../types';

export type Route =
  | { name: 'home' }
  | { name: 'quiz'; mode: QuizMode; categoryId?: CategoryId }
  | { name: 'signs' }
  | { name: 'signsLearn' }
  | { name: 'categories' }
  | { name: 'progress' }
  | { name: 'caregiver' }
  | { name: 'settings' }
  | { name: 'manager' }
  | { name: 'help' };

interface RouterContextValue {
  route: Route;
  depth: number;
  navigate: (route: Route) => void;
  back: () => void;
  home: () => void;
}

const RouterContext = createContext<RouterContextValue | null>(null);

export function RouterProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<Route[]>([{ name: 'home' }]);
  const stackRef = useRef<Route[]>(stack);

  const commit = useCallback((next: Route[]) => {
    stackRef.current = next;
    setStack(next);
  }, []);

  useEffect(() => {
    window.history.replaceState({ depth: 1 }, '');
    const onPop = (event: PopStateEvent) => {
      const raw = (event.state as { depth?: number } | null)?.depth;
      const depth = typeof raw === 'number' && raw >= 1 ? raw : 1;
      const next = stackRef.current.slice(0, depth);
      stackRef.current = next.length > 0 ? next : [{ name: 'home' }];
      setStack(stackRef.current);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback(
    (route: Route) => {
      const next = [...stackRef.current, route];
      window.history.pushState({ depth: next.length }, '');
      commit(next);
      window.scrollTo({ top: 0 });
    },
    [commit],
  );

  const back = useCallback(() => {
    if (stackRef.current.length > 1) {
      window.history.back();
    }
  }, []);

  const home = useCallback(() => {
    const next: Route[] = [{ name: 'home' }];
    window.history.pushState({ depth: 1 }, '');
    commit(next);
    window.scrollTo({ top: 0 });
  }, [commit]);

  const value = useMemo<RouterContextValue>(
    () => ({ route: stack[stack.length - 1], depth: stack.length, navigate, back, home }),
    [stack, navigate, back, home],
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterContextValue {
  const ctx = useContext(RouterContext);
  if (!ctx) throw new Error('useRouter must be used inside <RouterProvider>');
  return ctx;
}
